/**
 * Abrechnungs-Sektion eines Tenants (TenantDetailPage): Erlös/Kosten/Marge
 * der gewählten Periode, Nutzung vs. im Plan enthaltene Menge (das in
 * VdiSection referenzierte Usage-/Quota-Panel) und die kaufmännischen
 * Einstellungen (abrechenbar, zugesicherte Seats, Rabatt, Pauschale).
 *
 * Backend: GET /api/billing/tenants/:slug, GET/PUT …/config.
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BillingTerm, TenantBillingBreakdown, TenantBillingConfigInput, extractApiError, getTenantBilling, updateTenantBillingConfig } from '../services/api';
import { currentPeriodKey, formatCents, formatPercent } from '../services/format';
import { PeriodPicker } from './billing/PeriodPicker';
import { LineItemsTable } from './billing/LineItemsTable';
import { UsageTable } from './billing/UsageTable';

interface Props {
  slug: string;
  isReadOnly: boolean;
}

export function BillingSection({ slug, isReadOnly }: Props) {
  const [params, setParams] = useSearchParams();
  const period = params.get('period') ?? currentPeriodKey();
  const [data, setData] = useState<TenantBillingBreakdown | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<'usage' | 'revenue' | 'cost' | 'config'>('usage');
  const [form, setForm] = useState<{ billable: boolean; committedSeats: string; discountPct: string; flatPrice: string; billingTerm: BillingTerm; notes: string }>({
    billable: true, committedSeats: '', discountPct: '0', flatPrice: '', billingTerm: 'annual', notes: '',
  });

  function load(): void {
    getTenantBilling(slug, period).then((d) => {
      setData(d);
      setForm({
        billable: d.config.billable, committedSeats: d.config.committedSeats === null ? '' : String(d.config.committedSeats),
        discountPct: (d.config.discountBp / 100).toString(), flatPrice: d.config.flatPriceCents === null ? '' : (d.config.flatPriceCents / 100).toFixed(2),
        billingTerm: d.config.billingTerm ?? 'annual',
        notes: d.config.notes ?? '',
      });
    }).catch((e) => setError(extractApiError(e)));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [slug, period]);

  async function saveConfig(): Promise<void> {
    setBusy(true); setError(null);
    try {
      const input: TenantBillingConfigInput = {
        billable: form.billable,
        committedSeats: form.committedSeats.trim() === '' ? null : Number(form.committedSeats),
        discountBp: Math.round(Number(form.discountPct.replace(',', '.')) * 100),
        flatPriceCents: form.flatPrice.trim() === '' ? null : Math.round(Number(form.flatPrice.replace(',', '.')) * 100),
        billingTerm: form.billingTerm,
        notes: form.notes || null,
      };
      await updateTenantBillingConfig(slug, input);
      load();
    } catch (e) { setError(extractApiError(e)); } finally { setBusy(false); }
  }

  return (
    <div className="bg-white rounded shadow p-4 mb-6" id="billing">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h3 className="font-semibold">Abrechnung &amp; Kosten</h3>
        <PeriodPicker value={period} onChange={(k) => setParams((p) => { p.set('period', k); return p; }, { replace: true })} status={data?.periodStatus} />
      </div>
      {error && <div className="bg-red-100 text-red-700 p-2 rounded mb-2 text-sm">{error}</div>}
      {!data ? <p className="text-sm text-slate-500">Lade…</p> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <Stat label="Erlös (netto)" value={formatCents(data.revenue.netCents)} sub={`${data.seats.billable} Seats · ${data.activeModules.length} Module`} />
            <Stat label={`Selbstkosten (${data.cost.source === 'opencost' ? 'OpenCost' : 'Modell'})`} value={formatCents(data.cost.costCents)} sub={data.cost.source === 'opencost' ? `Modell: ${formatCents(data.cost.modelCents)}` : `CPU ${data.cost.basis.cpu === 'usage' ? 'gemessen' : 'Footprint'}, RAM ${data.cost.basis.ram === 'usage' ? 'gemessen' : 'Footprint'}, Storage ${data.cost.basis.storage === 'usage' ? 'gemessen' : 'Footprint'}`} />
            <Stat label="Deckungsbeitrag" value={formatCents(data.marginCents)} tone={data.marginCents < 0 ? 'bad' : undefined} />
            <Stat label="Marge" value={formatPercent(data.marginPercent)} sub={data.customer ? <Link to={`/customers/${data.customer.id}`} className="hover:underline">{data.customer.companyName}</Link> : <span className="text-amber-700">kein Kunde</span>} />
          </div>
          {data.warnings.length > 0 && (
            <ul className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-3 list-disc pl-5">
              {data.warnings.map((w) => <li key={w}>{w}</li>)}
            </ul>
          )}
          <div className="flex gap-1 border-b mb-3 text-sm">
            {([['usage', 'Nutzung & Quotas'], ['revenue', `Erlös-Positionen (${data.revenue.lines.length})`], ['cost', `Kosten-Positionen (${data.cost.lines.length})`], ['config', 'Einstellungen']] as const).map(([k, label]) => (
              <button key={k} type="button" onClick={() => setTab(k)} className={`px-3 py-1.5 border-b-2 -mb-px ${tab === k ? 'border-neutral-900 font-semibold' : 'border-transparent text-slate-500'}`}>{label}</button>
            ))}
          </div>
          {tab === 'usage' && (
            <div>
              <p className="text-xs text-slate-500 mb-2">
                Seats: Lizenz {data.seats.licenseSeats || '—'} · aktive Nutzer {data.seats.activeUsers ?? '—'} · zugesichert {data.seats.committedSeats ?? '—'} → abrechenbar <strong>{data.seats.billable}</strong>.
                Preisliste: {data.priceList ? <Link to={`/billing/price-lists/${data.priceList.id}`} className="underline">{data.priceList.name}</Link> : 'keine aktiv'}.
              </p>
              <UsageTable rows={data.usage} source={data.usageSource} />
            </div>
          )}
          {tab === 'revenue' && <LineItemsTable lines={data.revenue.lines} totalLabel="Netto" emptyText="Keine Erlös-Positionen (nicht abrechenbar oder keine Preisliste)." />}
          {tab === 'cost' && <LineItemsTable lines={data.cost.lines} totalLabel="Kosten" />}
          {tab === 'config' && (
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" checked={form.billable} disabled={isReadOnly} onChange={(e) => setForm({ ...form, billable: e.target.checked })} /> Abrechenbar (aus = interner/Test-Tenant)</label>
              <div />
              <div><label className="block text-xs text-slate-500 mb-1">Zugesicherte Seats (überschreibt Mindestabnahme)</label><input value={form.committedSeats} disabled={isReadOnly} onChange={(e) => setForm({ ...form, committedSeats: e.target.value })} placeholder="—" className="w-full px-2 py-1 border rounded font-mono" /></div>
              <div><label className="block text-xs text-slate-500 mb-1">Rabatt (%)</label><input value={form.discountPct} disabled={isReadOnly} onChange={(e) => setForm({ ...form, discountPct: e.target.value })} className="w-full px-2 py-1 border rounded font-mono" /></div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Vertragslaufzeit (Seat-Preis)</label>
                <select value={form.billingTerm} disabled={isReadOnly} onChange={(e) => setForm({ ...form, billingTerm: e.target.value as BillingTerm })} className="w-full px-2 py-1 border rounded">
                  <option value="annual">Jahresvertrag (Listenpreis, Mengenstaffel)</option>
                  <option value="monthly">Monatlich kündbar (Aufpreis)</option>
                </select>
              </div>
              <div><label className="block text-xs text-slate-500 mb-1">Pauschale je Monat (€, ersetzt Grundgebühr + Seats)</label><input value={form.flatPrice} disabled={isReadOnly} onChange={(e) => setForm({ ...form, flatPrice: e.target.value })} placeholder="—" className="w-full px-2 py-1 border rounded font-mono" /></div>
              <div><label className="block text-xs text-slate-500 mb-1">Notiz</label><input value={form.notes} disabled={isReadOnly} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-2 py-1 border rounded" /></div>
              <div className="md:col-span-2 flex justify-end"><button type="button" className="btn-primary" disabled={busy || isReadOnly} onClick={() => void saveConfig()}>{busy ? 'Speichere…' : 'Speichern'}</button></div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: React.ReactNode; tone?: 'bad' }) {
  return (
    <div className="bg-slate-50 rounded p-3">
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`text-lg font-mono ${tone === 'bad' ? 'text-red-700' : ''}`}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}
